import { useMemo } from "react";

import { ComparativeTable, IndicatorTable, scoreColor, scoreLabel } from "../../shared/"

import { createDecimalFormatter, textFormatter } from "../../../../../utils/formatter/formatter";

export const PerformanceAnalysis = ({ ctx }) => {

  const {
    productIndicatorData,
    tenantHierarchy,
    selectedProduct,
    productImageData,
    isLoadingProductImage,
  } = ctx;

  const { labels } = tenantHierarchy || {};

  const attributeTags = useMemo(() =>
    Object.entries(selectedProduct?.levels ?? {})
      .map(([key, val]) => [labels?.[key] ?? key, val])
      .filter(([_, val]) => val != null),
    [selectedProduct?.levels, labels]
  );

  const similarsBySeason = useMemo(() => {
    if (!selectedProduct?.similarity?.length) return {};
    return selectedProduct.similarity.reduce((acc, similar) => {
      const key = similar.seasonId;
      if (!acc[key]) acc[key] = [];
      acc[key].push(similar);
      return acc;
    }, {});
  }, [selectedProduct?.similarity]);

  const hasSimilar = useMemo(() => {
    if (!selectedProduct?.similarity?.length) return false;
    return selectedProduct.similarity.some(s => s.seasonId === selectedProduct.seasonId);
  }, [selectedProduct?.similarity, selectedProduct?.seasonId]);

  const productScoreStyle = scoreColor(selectedProduct?.score);

  if (!selectedProduct) return (
    <section style={{
      border: "1px solid #e5e5e5", borderRadius: "12px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)", overflow: "hidden",
      height: "calc(100vh - 280px)", display: "flex",
      alignItems: "center", justifyContent: "center", backgroundColor: "#fafafa"
    }}>
      <div style={{ textAlign: "center", color: "#ccc" }}>
        <div style={{ fontSize: "36px", marginBottom: 8 }}>🎯</div>
        <div style={{ fontSize: "13px" }}>Selecione um produto para ver a análise</div>
      </div>
    </section>
  );  

  return (
    <section style={{
      border: "1px solid #e5e5e5", borderRadius: "12px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
      display: "flex", flexDirection: "column",
      height: "calc(100vh - 280px)", overflow: "hidden"
    }}>
      <section style={{
        padding: "12px 16px", display: "flex", alignItems: "center",
        justifyContent: "space-between", borderBottom: "1px solid #f0f0f0",
        flexShrink: 0, backgroundColor: "#fff",}}>

        {/* Título da seção */}  
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <span style={{
            width: 26, height: 26, borderRadius: "50%",
            backgroundColor: "#E8773A", color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontWeight: "700", fontSize: "13px", flexShrink: 0,
          }}>3</span>
          <span style={{ fontWeight: "600", fontSize: "14px", color: "#1a1a1a" }}>
            Análise Detalhada
          </span>
        </div>
      </section>

      <section style={{ overflowY: "auto", flex: 1, backgroundColor: "#fafafa" }}>

        {/* Hierarquia do produto */}
        <div style={{ padding: "12px 16px", backgroundColor: "#fff", borderBottom: "1px solid #f0f0f0" }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {attributeTags?.map(([label, val]) => (
              <div key={label} style={{
                fontSize: "11px", color: "#666", border: "1px solid #e8e8e8",
                borderRadius: "20px", padding: "3px 10px", backgroundColor: "#f5f5f5", whiteSpace: "nowrap" }}>
                {label}: <strong style={{ color: "#1a1a1a" }}>{val}</strong>
              </div>
            ))}
          </div>
        </div>

        <div style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 14 }}>

          {/* cards: produto principal + similares lado a lado */}
          <div style={{ display: "grid", gridTemplateColumns: `1fr${hasSimilar ? ` repeat(${Math.min(selectedProduct.similarity.filter(s => s.seasonId === selectedProduct.seasonId).length, 3)}, 1fr)` : ''}`, gap: 12 }}>
            
            {/* produto principal */}
            <div style={{ padding: "16px", border: "1px solid #e8e8e8", borderRadius: "10px", backgroundColor: "#fff" }}>
              <h5 style={{ fontSize: "14px", fontWeight: "700", color: "#1a1a1a", marginBottom: 10 }}>
                Performance do Produto
              </h5>
              <p style={{ fontSize: "36px", fontWeight: "800", color: productScoreStyle?.color, lineHeight: 1 }}>
                {createDecimalFormatter({ minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(selectedProduct?.score)}
              </p>
              <p style={{ fontSize: "11px", color: "#bbb", marginTop: 2, marginBottom: 6 }}>de 10.00</p>
              <p style={{ fontSize: "12px", fontWeight: "700", color: productScoreStyle?.color, marginBottom: 6 }}>
                {scoreLabel(selectedProduct?.score)}
              </p>
              <p style={{ fontSize: "11px", color: "#555", lineHeight: 1.6 }}>
                O índice é calculado com base na venda, lucro e <strong>participação projetada</strong> para a próxima coleção na marca.
              </p>
            </div>

            {/* similares da season atual ao lado */}
            {hasSimilar && selectedProduct.similarity
              .filter(s => s.seasonId === selectedProduct.seasonId)
              .map(similar => {
                const simScoreStyle = scoreColor(similar?.estimatedScore ?? similar?.pastScore);
                const score = similar?.estimatedScore || similar?.pastScore;
                return (
                  <div key={similar.similarProductId} style={{ padding: "16px", border: "1px solid #e8e8e8", borderRadius: "10px", backgroundColor: "#fff" }}>
                    <h5 style={{ fontSize: "11px", fontWeight: "600", color: "#888", marginBottom: 8 }}>
                      Produto similar:
                    </h5>
                    <p style={{ fontSize: "13px", fontWeight: "700", color: "#1a1a1a", marginBottom: 8 }}>{similar.level8}</p>
                    <p style={{ fontSize: "11px", color: "#aaa", marginBottom: 8 }}>
                      {[similar?.level5, similar?.level6, similar?.level7].filter(Boolean).join(' · ')}
                    </p>
                    {score > 0 && (
                      <>
                        <p style={{ fontSize: "28px", fontWeight: "800", color: simScoreStyle.color, lineHeight: 1 }}>
                          {createDecimalFormatter({ minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(score)}
                        </p>
                        <p style={{ fontSize: "10px", color: "#bbb", marginBottom: 8 }}>de 10.00</p>
                        <p style={{ fontSize: "12px", fontWeight: "700", color: simScoreStyle.color }}>
                          {scoreLabel(score)}
                        </p>
                      </>
                    )}
                  </div>
                );
              })}
          </div>

          {/* matriz de indicadores */}
          <div style={{ padding: "16px", border: "1px solid #e8e8e8", borderRadius: "10px", backgroundColor: "#fff" }}>
            <div style={{ fontSize: "14px", fontWeight: "700", color: "#1a1a1a", marginBottom: 12 }}>
              {hasSimilar ? "Matriz Comparativa de Indicadores" : "Matriz do Produto Selecionado"}
            </div>
            {hasSimilar
              ? <ComparativeTable data={productIndicatorData} />
              : <IndicatorTable data={productIndicatorData} />
            }
          </div>

          {/* produto em análise — imagem */}
          <div style={{ padding: "16px", border: "1px solid #e8e8e8", borderRadius: "10px", backgroundColor: "#fff" }}>
            <h4 style={{ fontSize: "14px", fontWeight: "700", color: "#1a1a1a", marginBottom: 14 }}>
              {textFormatter("produto em análise")}
            </h4>
            <div style={{ maxWidth: 160 }}>

              <h5 style={{ fontSize: "10px", fontWeight: "700", color: "#E8773A", letterSpacing: "0.06em", marginBottom: 8 }}>
                {textFormatter("produto selecionado", { textTransform: 'uppercase' })}
              </h5>

              <section style={{ border: "1px solid #e8e8e8", borderRadius: "8px", overflow: "hidden", backgroundColor: "#fafafa" }}>
                {isLoadingProductImage ? (
                  <div style={{ blockSize: 120, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f5f5f5" }}>
                    <span style={{ fontSize: "11px", color: "#bbb" }}>Carregando...</span>
                  </div>
                ) : productImageData?.img ? (
                  <img 
                    src={`data:${productImageData.mimeType};base64,${productImageData.img}`} 
                    alt={selectedProduct.name}
                    style={{ blockSize: 120, objectFit: "contain", display: "block", backgroundColor: "#f5f5f5" }} />
                ) : (
                  <div style={{ blockSize: 120, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#ccc", fontSize: "11px", gap: 6, backgroundColor: "#f5f5f5" }}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5">
                      <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" />
                    </svg>
                  </div>
                )}
                <p style={{ padding: "6px 8px", fontSize: "11px", color: "#666", fontWeight: "500" }}>
                  {textFormatter(selectedProduct?.levels?.level8 ?? "")}
                </p>
              </section>
            </div>
          </div>

          {/* similares de seasons antigas */}
          {Object.entries(similarsBySeason)
            .filter(([seasonId]) => Number(seasonId) !== selectedProduct.seasonId)
            .sort(([a], [b]) => Number(b) - Number(a))
            .map(([seasonId, similars]) => (
              <div key={seasonId} style={{ padding: "16px", border: "1px solid #e8e8e8", borderRadius: "10px", backgroundColor: "#fff" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                  <h5 style={{ fontSize: "13px", fontWeight: "700", color: "#1a1a1a" }}>Produtos Similares</h5>
                  <span style={{ fontSize: "11px", color: "#E8773A", fontWeight: "600", backgroundColor: "#fff6f0", border: "1px solid #f4c4a6", borderRadius: "20px", padding: "2px 10px" }}>
                    {similars[0]?.seasonName}
                  </span>
                  <div style={{ flex: 1, height: 1, backgroundColor: "#f0f0f0" }} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(similars.length, 4)}, 1fr)`, gap: 10 }}>
                  {similars.map(similar => {
                    const similarImg = productImageData?.similarImages?.find(
                      s => s.productId === similar.similarProductId
                    );
                    const simScoreStyle = scoreColor(similar?.estimatedScore ?? similar?.pastScore);
                    const score = similar?.estimatedScore || similar?.pastScore;
                    return (
                      <div key={similar.similarProductId} style={{ border: "1px solid #f0f0f0", borderRadius: "8px", overflow: "hidden", backgroundColor: "#fafafa" }}>
                        {similarImg?.img ? (
                          <img src={`data:${similarImg.mimeType};base64,${similarImg.img}`} alt={similar.level8}
                            style={{ blockSize: 100, objectFit: "contain", display: "block", backgroundColor: "#f5f5f5" }} />
                        ) : (
                          <div style={{ blockSize: 100, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f5f5f5" }}>
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5">
                              <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" />
                            </svg>
                          </div>
                        )}
                        <div style={{ padding: "8px 10px" }}>
                          <p style={{ fontSize: "12px", color: "#444", fontWeight: "600", marginBottom: 2 }}>{similar.level8}</p>
                          <p style={{ fontSize: "10px", color: "#aaa", marginBottom: 6 }}>
                            {[similar?.level5, similar?.level6, similar?.level7].filter(Boolean).join(' · ')}
                          </p>
                          {score > 0 && (
                            <>
                              <p style={{ fontSize: "14px", fontWeight: "800", color: simScoreStyle.color }}>{score.toFixed(2)}</p>
                              <p style={{ fontSize: "10px", color: "#bbb" }}>de 10.00</p>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

        </div>        
      </section>
    </section>
  );
};